import type { BranchLinkData } from "./branch.types";
import { BranchNotificationType } from "./branch.types";

const INTERNAL_QUERY_KEYS = new Set([
  "_branch_match_id",
  "_branch_referrer",
  "~referring_browser",
]);

const normalizeDataKeys = (data: Record<string, string>): Record<string, string> => {
  const normalized = { ...data };

  if (!normalized.maintenanceTicketId) {
    normalized.maintenanceTicketId = data.maintenance_ticket_id ?? data.ticketId ?? "";
  }

  if (!normalized.pollId) {
    normalized.pollId = data.poll_id ?? "";
  }

  if (!normalized.livlyEventId) {
    normalized.livlyEventId = data.livly_event_id ?? data.eventId ?? "";
  }

  Object.keys(normalized).forEach((key) => {
    if (!normalized[key]) {
      delete normalized[key];
    }
  });

  return normalized;
};

const parseJsonDataParam = (value: string | null): Record<string, string> => {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, val]) => {
      if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
        acc[key] = String(val);
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const getNotificationType = (params: URLSearchParams): string => {
  return (
    params.get("notificationType") ??
    params.get("notification_type") ??
    params.get("type") ??
    ""
  );
};

const getEntityIds = (params: URLSearchParams): string[] => {
  const entityIdsRaw = params.get("entityIds") ?? params.get("entity_ids") ?? "";
  if (!entityIdsRaw) {
    return [];
  }

  return entityIdsRaw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

export const parseBranchLinkDataFromUrl = (url: string): BranchLinkData => {
  const parsedUrl = new URL(url);
  const params = parsedUrl.searchParams;
  const notificationType = getNotificationType(params);
  const entityIds = getEntityIds(params);
  const dataFromJson = parseJsonDataParam(params.get("data"));

  const queryData = Array.from(params.entries()).reduce<Record<string, string>>((acc, [key, value]) => {
    if (key === "notificationType" || key === "notification_type" || key === "type") {
      return acc;
    }

    if (key === "data" || INTERNAL_QUERY_KEYS.has(key)) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

  const data = normalizeDataKeys({
    ...dataFromJson,
    ...queryData,
  });

  if (!data.maintenanceTicketId && entityIds[0] && (
    notificationType === BranchNotificationType.TicketCreated ||
    notificationType === BranchNotificationType.TicketStatusChanged ||
    notificationType === BranchNotificationType.TicketChatMessageReceived
  )) {
    data.maintenanceTicketId = entityIds[0];
  }

  if (!data.pollId && entityIds[0] && notificationType === BranchNotificationType.ResidentPollPosted) {
    data.pollId = entityIds[0];
  }

  if (!data.livlyEventId && entityIds[0] && (
    notificationType === BranchNotificationType.LivlyEvent1HourReminder ||
    notificationType === BranchNotificationType.LivlyEventReminder
  )) {
    data.livlyEventId = entityIds[0];
  }

  return {
    notificationType,
    data: Object.keys(data).length > 0 ? data : null,
  };
};

/**
 * Maps Branch link data to an internal route path.
 *
 * @param linkData - Parsed data from redirect query parameters
 * @param leaseId - The user's current propertyUnitLeaseId
 * @param propertyId - The user's current propertyId
 * @returns The route path to navigate to, or home as fallback
 */
export const mapBranchRoute = (
  linkData: BranchLinkData,
  leaseId: number,
  propertyId: number
): string => {
  const { notificationType, data } = linkData;
  const base = `/lease/${leaseId}`;
  
  switch (notificationType) {
    // Rent
    case BranchNotificationType.AutoPayReminder:
    case BranchNotificationType.FirstMonthRentFailure:
    case BranchNotificationType.FirstMonthRentSuccess:
    case BranchNotificationType.RentSuccess:
    case BranchNotificationType.RentFailure:
    case BranchNotificationType.RentSuccessAutoPay:
      return `${base}/rent`;

    // Maintenance
    case BranchNotificationType.TicketCreated:
    case BranchNotificationType.TicketStatusChanged:
      if (data?.maintenanceTicketId) {
        return `${base}/maintenance/${propertyId}/${data.maintenanceTicketId}`;
      }
      return `${base}/maintenance/${propertyId}`;

    case BranchNotificationType.TicketChatMessageReceived:
      if (data?.maintenanceTicketId) {
        return `${base}/maintenance/${propertyId}/${data.maintenanceTicketId}/chat`;
      }
      return `${base}/maintenance/${propertyId}`;

    // Insurance
    case BranchNotificationType.InsuranceProofApproved:
    case BranchNotificationType.InsuranceProofDenied:
    case BranchNotificationType.InsurancePaymentFailure:
    case BranchNotificationType.InsurancePaymentSuccessAutoPay:
      return `${base}/my-lease`;

    // Community Feed
    case BranchNotificationType.CommunityFeedPropertyManagerPost:
    case BranchNotificationType.CommunityFeedUserLike:
    case BranchNotificationType.CommunityFeedLivlyPost:
    case BranchNotificationType.CommunityFeedGroupPost:
    case BranchNotificationType.CommunityFeedResidentPost:
    case BranchNotificationType.CommunityFeedUserComment:
      return `${base}/community-feed`;

    // Guests
    case BranchNotificationType.GuestPassApprovalNotification:
      return `${base}/guests`;

    // Polls
    case BranchNotificationType.ResidentPollPosted:
      if (data?.pollId) {
        return `${base}/my-feedback/polls/${data.pollId}`;
      }
      return `${base}/my-feedback`;

    // Feedback
    case BranchNotificationType.AmenityBookingFeedbackReminder:
    case BranchNotificationType.LivlyEventFeedbackReminder:
    case BranchNotificationType.MoveInFeedbackReminder:
    case BranchNotificationType.ResidentFeedbackReplyReceived:
      return `${base}/my-feedback`;

    // Events
    case BranchNotificationType.LivlyEvent1HourReminder:
    case BranchNotificationType.LivlyEventReminder:
      if (data?.livlyEventId) {
        return `${base}/events/property/${propertyId}/${data.livlyEventId}`;
      }
      return `${base}/events/property/${propertyId}`;

    // Types that navigate to home
    case BranchNotificationType.CommunityFeedPermissionRevoked:
    case BranchNotificationType.KioskGuestNotification:
      return `${base}/home`;

    // No web equivalent — fall through to home
    case BranchNotificationType.ResidentNewPackage:
    case BranchNotificationType.ResidentPackageDelivered:
    case BranchNotificationType.ResidentChatMessageReceived:
    case BranchNotificationType.AmbassadorApplicationApproved:
    case BranchNotificationType.AmbassadorApplicationDenied:
    case BranchNotificationType.AmbassadorConversationEnded:
    case BranchNotificationType.AmbassadorOpportunityCreated:
    case BranchNotificationType.AmbassadorMessageReceived:
    default:
      return `${base}/home`;
  }
};
